import {
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { useChangelogApi } from '../../services/api/useChangelog.api';
import { ApiResponse } from '../../types/ApiResponse.type';
import { Changelog } from '../../types/Changelog/Changelog.type';
import ChangelogRow from './ChangelogRow';

interface ChangelogsTableProps {
  snackbarData: (message: string, severity: 'success' | 'error') => void;
  onEdit: (changelogId: number) => void;
}

function ChangelogsTable({ snackbarData, onEdit }: ChangelogsTableProps) {
  const queryClient = useQueryClient();
  const { getChangelogs, deleteChangelog } = useChangelogApi();
  const { ref: changelogsRef, inView: changelogsInView } = useInView();

  const {
    data: infiniteChangelogs,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['changelogs'],
    queryFn: ({ pageParam = 1 }) => getChangelogs({ page: pageParam, per_page: 10, sort: 'release_date:desc' }),
    initialPageParam: 1,
    getNextPageParam: (lastPage: ApiResponse<Changelog[]>) => {
      if ((lastPage?.meta.pagination?.page ?? 0) < (lastPage?.meta.pagination?.pages ?? 0)) {
        return (lastPage?.meta.pagination?.page ?? 0) + 1;
      }
      return undefined;
    },
  });

  const changelogs = infiniteChangelogs?.pages?.flatMap((page) => page.data) ?? [];

  const deleteMutation = useMutation({
    mutationFn: (changelogId: number) => deleteChangelog(changelogId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['changelogs'] });
      snackbarData('Changelog deleted successfully!', 'success');
    },
    onError: (error) => {
      snackbarData(error?.message || 'Failed to delete changelog. Please try again.', 'error');
    },
  });

  const handleDelete = (changelogId: number) => {
    deleteMutation.mutate(changelogId);
  };

  useEffect(() => {
    if (changelogsInView && fetchNextPage && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [changelogsInView, fetchNextPage, hasNextPage, isFetchingNextPage]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ border: '1px solid #cbd5e0', boxShadow: 'none', maxHeight: 400 }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Release Date</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Items</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {changelogs.map((changelog: Changelog) => (
            <ChangelogRow
              key={changelog.id}
              changelog={changelog}
              onEdit={onEdit}
              onDelete={handleDelete}
              isDeleting={deleteMutation.isPending}
            />
          ))}
          <TableRow ref={changelogsRef}>
            <TableCell colSpan={5} sx={{ border: 'none', padding: 1 }}>
              {isFetchingNextPage && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    py: 1,
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default ChangelogsTable;
